import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Sparkles,
  Sun,
  Wind,
  Trees,
  Wallet,
  ShieldCheck,
  Clock,
  Wrench,
  HeartHandshake,
} from "lucide-react";
import PageHero from "@/components/PageHero";
import SEO from "@/components/SEO";

/* =====================================================================
   Page : Conciergerie Mobile Home, Ambon (Morbihan, Sud Bretagne)
   Volontairement sans nommer de camping spécifique : nous prenons en
   charge tous les mobile homes dans tous les domaines & PRL d'Ambon
   et de la côte sud morbihannaise.
   ===================================================================== */

const ADVANTAGES = [
  {
    icon: Wallet,
    title: "Rentabilisez votre mobile home toute l'année",
    text: "Au lieu de laisser votre résidence vide 9 mois sur 12, transformez chaque semaine en revenus locatifs. Ambon, la côte sauvage de Pénerf et le Morbihan attirent des voyageurs d'avril à octobre, week-ends compris, grâce au microclimat exceptionnel.",
  },
  {
    icon: ShieldCheck,
    title: "Une personne de ménage attitrée, comme à la maison",
    text: "Le même membre de notre équipe Sud Bretagne veille sur votre mobile home à chaque rotation. Pas de roulement, pas de prestataires anonymes. La moindre anomalie est détectée immédiatement et signalée.",
  },
  {
    icon: Clock,
    title: "Mise en ligne sous 24 heures",
    text: "Photos professionnelles, descriptif optimisé, multidiffusion Airbnb · Booking · Vrbo · Le Bon Coin Vacances. Votre mobile home est visible et réservable en moins d'une journée.",
  },
  {
    icon: Wrench,
    title: "Maintenance et petits travaux assurés",
    text: "Remplacement d'ampoule, fuite d'eau, climatisation à recharger ? Notre équipe technique locale intervient rapidement, sans perturber les locataires.",
  },
  {
    icon: HeartHandshake,
    title: "Accueil voyageurs 24h/24, 7j/7",
    text: "Permanence de nuit incluse. Nos City Managers Sud Bretagne répondent aux questions, gèrent les arrivées, les départs et les imprévus, en français et en anglais.",
  },
  {
    icon: Sparkles,
    title: "Commission unique de 15 % TTC",
    text: "Pas de frais cachés, pas de surprise en fin de mois. Vous encaissez directement les revenus, nous facturons la conciergerie en une seule ligne claire.",
  },
];

const REVENUE_EXAMPLES = [
  { period: "Été (juillet–août)", price: "100–135 €", note: "Selon le couchage et la configuration" },
  { period: "Vacances scolaires & ponts", price: "75–105 €", note: "Mai, Toussaint, Pâques" },
  { period: "Week-ends hors saison", price: "55–80 €", note: "Avril, juin, septembre, octobre" },
  { period: "Saison complète typique", price: "12 000–20 000 €", note: "Revenu locatif annuel constaté" },
];

const FAQ_MOBILHOME = [
  {
    q: "Puis-je continuer à utiliser mon mobile home pour mes vacances personnelles ?",
    a: "Bien sûr. Vous nous transmettez à l'avance vos dates de réservation personnelle, et nous bloquons le calendrier en conséquence. Vos derniers locataires partent, notre équipe nettoie, et vous arrivez dans un mobile home impeccable.",
  },
  {
    q: "Couvrez-vous tous les campings, domaines et PRL d'Ambon et alentour ?",
    a: "Oui : nous prenons en charge la conciergerie de votre mobile home dans l'ensemble des campings, domaines et parcs résidentiels de loisirs d'Ambon, Damgan, Pénerf, Billiers, Muzillac et la côte sauvage du Morbihan. Notre équipe se déplace sur tout le secteur, en moins de 15 minutes en moyenne.",
  },
  {
    q: "Comment fonctionne la caution ? Mon mobile home est-il assuré ?",
    a: "Sur Airbnb, la garantie AirCover couvre les dommages jusqu'à 1 000 000 €. Sur Booking et Vrbo, nous prenons une empreinte de carte bancaire (entre 400 € et 1 500 € selon votre bien). En cas de dégradation, nous documentons, photographions et activons l'assurance plateforme, vous n'avez rien à gérer.",
  },
  {
    q: "Faut-il accepter les conditions du camping ou du domaine pour louer en Airbnb ?",
    a: "Oui, chaque camping, domaine ou PRL a ses propres règles de sous-location. Avant toute mise en ligne, nous vérifions le règlement intérieur de votre établissement et nous coordonnons l'accord avec la direction. Notre équipe Sud Bretagne a déjà l'habitude de travailler avec les principaux gestionnaires d'Ambon et de la côte morbihannaise.",
  },
  {
    q: "Le ménage et le linge sont-ils inclus ?",
    a: "Le ménage de qualité hôtelière entre chaque séjour est inclus dans la formule à 15 % TTC. Le linge (draps + serviettes) est en option à +3 % de commission supplémentaire (formule à 18 % TTC), les voyageurs apprécient énormément ce service haut de gamme.",
  },
];

const MobilHomeAmbon = () => {
  return (
    <div data-testid="page-mobil-home-ambon">
      <SEO
        title="Conciergerie pour mobile home à Ambon, Morbihan Sud Bretagne | Terre et Mer"
        description="Conciergerie pour mobile home à Ambon : gestion complète de votre résidence mobile dans les campings et domaines du Morbihan sud. Mise en location Airbnb, ménage, accueil 24h/24. Commission 15%, jusqu'à 20 000 €/an."
        keywords="conciergerie pour mobile home Ambon, conciergerie mobile home Ambon, conciergerie mobile home Morbihan, location mobile home Ambon Airbnb, conciergerie mobile home Damgan Pénerf, sous-location mobile home Sud Bretagne, mettre son mobile home en location Ambon, conciergerie camping Morbihan, gestionnaire mobile home Sud Bretagne, conciergerie mobile home côte sauvage Morbihan"
        breadcrumbs={[
          { name: "Accueil", path: "/" },
          { name: "Services", path: "/services" },
          { name: "Conciergerie mobile home Ambon", path: "/mobil-home-ambon" },
        ]}
      />

      <PageHero
        overline="Conciergerie pour mobile home"
        title="Conciergerie mobile home à Ambon"
        italic="entre de bonnes mains"
        description="Tous les campings, domaines et parcs résidentiels de loisirs d'Ambon, Damgan-Pénerf et de la côte sud morbihannaise : nous prenons en charge la mise en location de votre mobile home, l'accueil des voyageurs et son entretien. Vous gardez la liberté d'en profiter quand vous voulez."
        image="/images/mobil-home.jpg"
        cta={{ to: "/contact", label: "Étude gratuite de mon mobile home" }}
      />

      {/* Ambon, Section dédiée, Dark navy, image left, glass card */}
      <section
        className="py-20 lg:py-28 bg-ink text-white relative overflow-hidden"
        data-testid="ambon-mobilhome-section"
      >
        <div className="absolute -top-32 -left-32 w-[480px] h-[480px] bg-gold/15 rounded-full blur-[160px] pointer-events-none" />
        <div className="absolute -bottom-32 -right-32 w-[420px] h-[420px] bg-emerald-500/10 rounded-full blur-[140px] pointer-events-none" />

        <div className="container-tm relative">
          <div className="text-center mb-12">
            <span className="overline text-gold inline-flex items-center gap-2 justify-center">
              <Trees size={12} strokeWidth={2} />
              Notre fief Sud Bretagne, Ambon, côte sauvage du Morbihan
            </span>
            <h2 className="mt-5 font-serif font-light text-white text-4xl md:text-5xl lg:text-6xl leading-[1.04] tracking-tight max-w-3xl mx-auto">
              Ambon,
              <span className="italic text-gold"> notre terrain de jeu</span>.
            </h2>
          </div>

          <div className="grid lg:grid-cols-12 gap-0 items-stretch bg-white/[0.04] backdrop-blur-md border border-white/10 rounded-3xl overflow-hidden shadow-lift">
            <div className="lg:col-span-6 relative aspect-[4/3] lg:aspect-auto lg:min-h-[520px]">
              <img
                src="/images/ambon.jpg"
                alt="Longère en pierre d'Ambon, côte sauvage du Morbihan, secteur d'intervention Terre et Mer Conciergerie pour mobile homes"
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-ink/70 via-ink/15 to-transparent pointer-events-none" />

              <div className="absolute bottom-6 left-6 bg-ink/80 backdrop-blur-md rounded-2xl px-5 py-4 border border-gold/20">
                <p className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">
                  Intervention
                </p>
                <p className="font-serif text-2xl text-white leading-none mt-1">15 min</p>
              </div>
            </div>

            <div className="lg:col-span-6 p-8 md:p-10 lg:p-14 flex flex-col justify-center">
              <p className="font-sans text-base md:text-lg text-white/75 leading-relaxed">
                Avec ses <strong className="text-white">plages familiales</strong> (Bétahon, Lanedan, Brouel), ses <strong className="text-white">marais salants</strong>, son port ostréicole de <strong className="text-white">Pénerf</strong> et son arrière-pays bocager, Ambon est l'un des secrets les mieux gardés du Morbihan. La commune et ses voisines (Damgan, Billiers, Muzillac) concentrent <strong className="text-white">plusieurs campings 4-5★ et domaines de plein air</strong> où des centaines de propriétaires possèdent un mobile home qu'ils n'occupent que quelques semaines par an.
              </p>

              <p className="mt-5 font-sans text-base md:text-lg text-white/65 leading-relaxed">
                Notre équipe Sud Bretagne, basée localement, connaît parfaitement les établissements du secteur : règlements intérieurs, voisinage, équipements communs, accès. Nous y intervenons toutes les semaines, ce qui nous permet d'optimiser le calendrier de location et la qualité d'accueil de chaque voyageur.
              </p>

              <div className="mt-9 grid sm:grid-cols-1 gap-3 pt-7 border-t border-white/10">
                {[
                  "Connaissance des règlements intérieurs des campings et PRL d'Ambon et alentour",
                  "Accès rapide pour ménage, linge et maintenance (intervention en 15 min)",
                  "Optimisation calendrier en lien avec votre usage personnel",
                ].map((item) => (
                  <div
                    key={item}
                    className="flex items-start gap-3"
                  >
                    <div className="w-7 h-7 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center shrink-0 mt-0.5">
                      <CheckCircle2
                        size={13}
                        strokeWidth={2.2}
                        className="text-gold"
                      />
                    </div>
                    <span className="font-sans text-sm md:text-[15px] text-white/85 leading-snug">
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Avantages */}
      <section className="py-20 lg:py-28 bg-white" data-testid="advantages-section">
        <div className="container-tm">
          <div className="max-w-3xl mb-14">
            <span className="overline">Notre approche</span>
            <h2 className="mt-5 font-serif font-light text-ink text-4xl md:text-5xl lg:text-[52px] leading-[1.04] tracking-tight">
              Pourquoi confier votre mobile home
              <span className="italic text-gold"> à Terre et Mer</span> ?
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {ADVANTAGES.map((a, i) => {
              const Icon = a.icon;
              return (
                <div
                  key={a.title}
                  data-testid={`mobilhome-advantage-${i}`}
                  className="bg-sand/50 border border-gold/15 rounded-2xl p-7 hover:shadow-soft transition-shadow"
                >
                  <div className="w-11 h-11 rounded-xl bg-gold/15 border border-gold/25 flex items-center justify-center mb-5">
                    <Icon size={20} strokeWidth={1.7} className="text-gold" />
                  </div>
                  <p className="font-serif text-xl text-ink leading-snug">{a.title}</p>
                  <p className="mt-3 font-sans text-sm text-ink/65 leading-relaxed">
                    {a.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Revenus indicatifs */}
      <section
        className="py-20 lg:py-28 bg-ink text-white relative overflow-hidden"
        data-testid="revenue-section"
      >
        <div className="absolute -top-20 -right-20 w-[420px] h-[420px] bg-gold/15 rounded-full blur-[140px] pointer-events-none" />
        <div className="container-tm relative">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <span className="overline text-gold inline-flex items-center gap-2">
                <Sun size={12} strokeWidth={2} />
                Potentiel locatif réel
              </span>
              <h2 className="mt-5 font-serif font-light text-white text-4xl md:text-5xl lg:text-[52px] leading-[1.05] tracking-tight">
                Combien rapporte
                <span className="italic text-gold"> un mobile home</span> à Ambon ?
              </h2>
              <p className="mt-7 font-sans text-base md:text-lg text-white/65 leading-relaxed">
                Voici les fourchettes de prix réellement constatées sur les mobile homes de qualité dans les campings et domaines d'Ambon, Damgan et de la côte sud morbihannaise, sur l'ensemble de la saison Avril-Octobre. Les revenus dépendent du nombre de couchages, de la configuration (terrasse, climatisation, équipements) et de l'état général de votre bien.
              </p>
              <Link
                to="/contact"
                className="btn-accent group mt-8 inline-flex"
                data-testid="mobilhome-cta-revenue"
              >
                Étude personnalisée gratuite
                <ArrowRight
                  size={14}
                  strokeWidth={2}
                  className="ml-3 group-hover:translate-x-1 transition-transform"
                />
              </Link>
            </div>

            <div className="lg:col-span-7">
              <div className="bg-white/[0.04] backdrop-blur-md border border-white/10 rounded-3xl p-7 md:p-9 shadow-lift">
                {REVENUE_EXAMPLES.map((row, i) => (
                  <div
                    key={row.period}
                    className={`flex items-center justify-between py-5 ${
                      i < REVENUE_EXAMPLES.length - 1 ? "border-b border-white/10" : ""
                    }`}
                  >
                    <div>
                      <p className="font-serif text-lg text-white">{row.period}</p>
                      <p className="text-xs text-white/55 mt-1">{row.note}</p>
                    </div>
                    <p
                      className="font-serif text-xl md:text-2xl text-gold tabular-nums"
                      data-testid={`revenue-row-${i}`}
                    >
                      {row.price}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-28 bg-cream" data-testid="mobilhome-faq-section">
        <div className="container-tm max-w-4xl">
          <div className="mb-14">
            <span className="overline">Questions fréquentes</span>
            <h2 className="mt-5 font-serif font-light text-ink text-4xl md:text-5xl lg:text-[52px] leading-[1.04] tracking-tight">
              Tout ce que les propriétaires
              <span className="italic text-gold"> de mobile home</span> nous demandent.
            </h2>
          </div>

          <div className="space-y-4">
            {FAQ_MOBILHOME.map((item, i) => (
              <details
                key={item.q}
                data-testid={`mobilhome-faq-${i}`}
                className="group bg-white border border-gold/15 rounded-2xl p-6 md:p-7 transition-shadow hover:shadow-soft open:shadow-soft"
              >
                <summary className="flex items-start justify-between gap-4 cursor-pointer list-none">
                  <p className="font-serif text-lg md:text-xl text-ink leading-snug">
                    {item.q}
                  </p>
                  <span className="w-7 h-7 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center shrink-0 mt-1 group-open:rotate-45 transition-transform">
                    <span className="text-gold text-lg leading-none">+</span>
                  </span>
                </summary>
                <p className="mt-4 font-sans text-sm md:text-base text-ink/70 leading-relaxed">
                  {item.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section
        className="py-20 lg:py-24 bg-ink text-white relative overflow-hidden"
        data-testid="mobilhome-final-cta"
      >
        <div className="absolute -top-20 -right-20 w-[420px] h-[420px] bg-gold/15 rounded-full blur-[140px] pointer-events-none" />
        <div className="container-tm relative max-w-3xl text-center">
          <span className="overline text-gold inline-flex items-center gap-2">
            <Wind size={12} strokeWidth={2} />
            Prêt à passer le cap ?
          </span>
          <h2 className="mt-5 font-serif font-light text-white text-4xl md:text-5xl lg:text-[52px] leading-[1.05] tracking-tight">
            Étude gratuite & sans engagement
            <span className="italic text-gold"> sous 24 h</span>.
          </h2>
          <p className="mt-7 font-sans text-base md:text-lg text-white/70 leading-relaxed">
            Notre City Manager Sud Bretagne étudie votre mobile home, son emplacement dans le camping ou domaine, son potentiel et vous propose un plan personnalisé. Aucune pression, aucun engagement.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="btn-accent group" data-testid="mobilhome-cta-contact">
              Lancer mon étude gratuite
              <ArrowRight
                size={14}
                strokeWidth={2}
                className="ml-3 group-hover:translate-x-1 transition-transform"
              />
            </Link>
            <Link to="/pourquoi-nous" className="btn-secondary" data-testid="mobilhome-cta-pourquoi">
              Comparer nos services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MobilHomeAmbon;
